import { Popover } from '@material-ui/core';
import React, { useState } from 'react';
import nameSort from '../utils/nameSort';
import { CellOrder, TableItem } from '../elements/SimpleTable';
import CreateNameForm, { FormValues } from './CreateNameForm';
import EnhancedTable from './EnhancedTable';
import {
	useCreateItemSubcategory,
	useDeleteItemSubcategory,
	useMe,
} from '../hooks';
import { useSnackbar } from 'notistack';
import { v4 as uuid } from 'uuid';
import { Query } from '../typings/graphql';
import { extractErrorMessage } from '../utils/errorMessages';
import { useQuery } from '@apollo/client/react/hooks/useQuery';
import { gql } from '@apollo/client/core';

export const QUERY = gql`
	query itemCategories {
		itemCategories {
			id
			name
			subcategories {
				id
				name
			}
		}
	}
`;

const cellOrder: CellOrder[] = [
	{
		align: 'left',
		name: 'Subcategory',
		itemName: 'name',
	},
];

type Props = {
	category: string;
	onSelection?: (item: TableItem) => void;
};

const ItemSubcategoriesTable = ({
	category,
	onSelection,
}: Props): JSX.Element => {
	const createItemSubcategory = useCreateItemSubcategory();
	const deleteItemSubcategory = useDeleteItemSubcategory();
	const { enqueueSnackbar } = useSnackbar();
	const me = useMe();
	const { data, refetch } = useQuery<Pick<Query, 'itemCategories'>>(QUERY);

	const itemCategories = data?.itemCategories;
	const subcategories = itemCategories?.find(
		(itemCategory) => itemCategory.id === category
	)?.subcategories;

	const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

	const handleTriggerPopover = (
		event: React.MouseEvent<HTMLButtonElement>
	): void => setAnchorEl(event.currentTarget);

	const handlePopoverClose = (): void => setAnchorEl(null);

	const handleSubmit = async (values: FormValues): Promise<void> => {
		const variables = {
			itemSubcategory: {
				id: uuid(),
				name: values.name,
				itemCategoryId: category,
			},
		};
		try {
			await createItemSubcategory(variables);
			enqueueSnackbar(`Successfully created item subcategory: ${values.name}`, {
				variant: 'success',
			});
		} catch (error) {
			const message = extractErrorMessage(error);
			enqueueSnackbar(message, { variant: 'error' });
		}
	};

	const handleDelete = async (item: TableItem): Promise<void> => {
		try {
			await deleteItemSubcategory({ id: item.id });
			enqueueSnackbar(`Successfully deleted item category: ${item.name}`, {
				variant: 'info',
			});
		} catch (error) {
			const message = extractErrorMessage(error);
			enqueueSnackbar(message, { variant: 'error' });
		}
	};

	const handleListItemClick = (item: TableItem): void => {
		onSelection && onSelection(item);
	};

	const sorted = subcategories?.slice(0).sort(nameSort);

	const open = Boolean(anchorEl);
	const id = open ? 'create-item-subcategories-popover' : undefined;

	return (
		<>
			<EnhancedTable
				empty="No item subcategories exists yet"
				reload={refetch}
				items={sorted ? (sorted as TableItem[]) : []}
				searchPlaceholder="Search for item subcategory name"
				order={cellOrder}
				onClick={handleTriggerPopover}
				onListItemClick={handleListItemClick}
				onDeleteClick={handleDelete}
				canCreate={me?.admin}
				canDelete={me?.admin}
			/>
			<Popover
				id={id}
				open={open}
				anchorEl={anchorEl}
				onClose={handlePopoverClose}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
				transformOrigin={{ vertical: 'top', horizontal: 'center' }}
			>
				<CreateNameForm
					label="Item Subcategory Name"
					onSubmit={handleSubmit}
					onClose={handlePopoverClose}
				/>
			</Popover>
		</>
	);
};

export default ItemSubcategoriesTable;
